import { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  Stack,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { useUserSchedules, useScheduleJobs } from '@/hooks/useUsers';
import { formatOrderDate } from '@liminal/utils/date';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { StatusPill, type StatusPillTone } from '@liminal/web-ui/primitives/StatusPill';
import { MonoCell } from '@/components/ui/MonoCell';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import type { PaymentSchedule, PaymentScheduleJob } from '@/api/types';

function scheduleStatusVariant(status?: string): StatusPillTone {
  if (!status) return 'neutral';
  if (status.includes('ACTIVE')) return 'success';
  if (status.includes('PAUSED')) return 'warning';
  if (status.includes('DELETED')) return 'error';
  return 'neutral';
}

function scheduleStatusLabel(status?: string): string {
  if (!status) return 'Unknown';
  return status.replace('SCHEDULE_STATUS_', '');
}

function jobStatusVariant(status?: string): StatusPillTone {
  if (!status) return 'neutral';
  if (status.includes('SUCCESS')) return 'success';
  if (status.includes('PENDING')) return 'warning';
  if (status.includes('FAILED')) return 'error';
  return 'neutral';
}

function jobStatusLabel(status?: string): string {
  if (!status) return 'Unknown';
  return status.replace('JOB_STATUS_', '');
}

const JOB_SORT_ACCESSORS = {
  status: (job: PaymentScheduleJob) => jobStatusLabel(job.status),
  effectiveAt: (job: PaymentScheduleJob) => job.effectiveAt,
  executedAt: (job: PaymentScheduleJob) => job.executedAt,
  retryCount: (job: PaymentScheduleJob) => job.retryCount ?? 0,
  error: (job: PaymentScheduleJob) => job.error,
};

function ScheduleRow({ schedule, userId }: { schedule: PaymentSchedule; userId: string }) {
  const [expanded, setExpanded] = useState(false);
  const { data: jobsData, isLoading: jobsLoading } = useScheduleJobs(
    userId,
    expanded ? (schedule.id ?? '') : '',
  );

  // Every job for one schedule comes back together, so this is a true sort.
  const { rows: jobs, sort: jobSort } = useTableSort(
    jobsData?.jobs ?? [],
    JOB_SORT_ACCESSORS,
    'effectiveAt',
    'desc',
  );

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton
            icon={expanded ? 'chevron-down' : 'chevron-forward'}
            label={expanded ? 'Collapse payment details' : 'Expand payment details'}
            size="sm"
            onClick={() => setExpanded(!expanded)}
          />
        </TableCell>
        <TableCell>{schedule.name || '\u2014'}</TableCell>
        <TableCell>{schedule.amount ?? '\u2014'} {schedule.currency ?? ''}</TableCell>
        <MonoCell>{schedule.cron || '\u2014'}</MonoCell>
        <TableCell>
          <StatusPill
            label={scheduleStatusLabel(schedule.status)}
            tone={scheduleStatusVariant(schedule.status)}
            size="sm"
          />
        </TableCell>
        <TableCell>{schedule.nextPayment ? formatOrderDate(schedule.nextPayment) : '\u2014'}</TableCell>
        <TableCell>{schedule.createdAt ? formatOrderDate(schedule.createdAt) : '\u2014'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, pl: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Job History</Typography>
              {jobsLoading ? (
                <Skeleton height={80} />
              ) : jobs.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableCell column="status" sort={jobSort}>
                        Status
                      </SortableTableCell>
                      <SortableTableCell column="effectiveAt" sort={jobSort}>
                        Effective At
                      </SortableTableCell>
                      <SortableTableCell column="executedAt" sort={jobSort}>
                        Executed At
                      </SortableTableCell>
                      <SortableTableCell column="retryCount" sort={jobSort}>
                        Retries
                      </SortableTableCell>
                      <SortableTableCell column="error" sort={jobSort}>
                        Error
                      </SortableTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jobs.map((job, ji) => (
                      <TableRow key={job.id ?? ji}>
                        <TableCell>
                          <StatusPill
                            label={jobStatusLabel(job.status)}
                            tone={jobStatusVariant(job.status)}
                            size="sm"
                          />
                        </TableCell>
                        <TableCell>{job.effectiveAt ? formatOrderDate(job.effectiveAt) : '\u2014'}</TableCell>
                        <TableCell>{job.executedAt ? formatOrderDate(job.executedAt) : '\u2014'}</TableCell>
                        <TableCell>{job.retryCount ?? 0}</TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'error.main' }}>
                          {job.error || '\u2014'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.disabled">No jobs yet.</Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const SCHEDULE_COLUMN_COUNT = 7;

/** One user's whole schedule list arrives per response, so this is a true sort. */
const SCHEDULE_SORT_ACCESSORS = {
  name: (schedule: PaymentSchedule) => schedule.name,
  amount: (schedule: PaymentSchedule) => schedule.amount,
  cron: (schedule: PaymentSchedule) => schedule.cron,
  status: (schedule: PaymentSchedule) => scheduleStatusLabel(schedule.status),
  nextPayment: (schedule: PaymentSchedule) => schedule.nextPayment,
  createdAt: (schedule: PaymentSchedule) => schedule.createdAt,
};

export function ScheduledPaymentsSection({ userId }: { userId: string }) {
  const { data: schedulesData, isLoading, isError } = useUserSchedules(userId);

  const { rows: schedules, sort } = useTableSort(
    schedulesData?.schedules ?? [],
    SCHEDULE_SORT_ACCESSORS,
    'createdAt',
    'desc',
  );

  return (
    <Stack spacing={3}>
      <Card variant="elevated" padding="none">
          <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
            Scheduled Payments
            <Typography component="span" variant="body2" color="text.disabled" sx={{ ml: 1 }}>
              ({schedules.length})
            </Typography>
          </Typography>
          {/* The header now renders in every state. It used to appear only
              when there were rows, so a loading or empty section gave no clue
              what it was about to show — and a failed fetch showed nothing at
              all. */}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                <SortableTableCell column="name" sort={sort}>
                  Name
                </SortableTableCell>
                <SortableTableCell column="amount" sort={sort}>
                  Amount
                </SortableTableCell>
                <SortableTableCell column="cron" sort={sort}>
                  Schedule
                </SortableTableCell>
                <SortableTableCell column="status" sort={sort}>
                  Status
                </SortableTableCell>
                <SortableTableCell column="nextPayment" sort={sort}>
                  Next Payment
                </SortableTableCell>
                <SortableTableCell column="createdAt" sort={sort}>
                  Created
                </SortableTableCell>
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={SCHEDULE_COLUMN_COUNT}
              isLoading={isLoading}
              isError={isError}
              isEmpty={schedules.length === 0}
              loadingRows={3}
              emptyMessage="No scheduled payments."
              errorMessage="Could not load scheduled payments."
            >
              {schedules.map((s, i) => (
                <ScheduleRow key={s.id ?? i} schedule={s} userId={userId} />
              ))}
            </DataTableBody>
          </Table>
      </Card>
    </Stack>
  );
}
